import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Globe, FileText, TrendingUp, Users, 
  Target, Briefcase, Award, Zap, Shield,
  ChevronRight, Clock, Phone, FileCheck, BarChart3, 
  PieChart, Lightbulb, Search, Calculator
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function WhiteGloveService() {
  const [premiumPrice, setPremiumPrice] = useState(1999);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    async function fetchPrice() {
      try {
        const res = await axios.get(API + '/payments/info');
        const packages = res.data.packages || {};
        // packages is an object, not array
        if (packages.premium && packages.premium.price) {
          setPremiumPrice(packages.premium.price);
        }
      } catch (err) {
        console.error('Failed to fetch price:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchPrice();
  }, []);

  const services = [
    { icon: Lightbulb, title: "Complete Business Idea Review", description: "Comprehensive evaluation of your business concept for UK market fit" },
    { icon: Zap, title: "Innovation Assessment", description: "Analysis of how innovative and unique your idea is for endorsement" },
    { icon: Target, title: "Viability Assessment", description: "Detailed assessment of your business viability and sustainability" },
    { icon: TrendingUp, title: "Scalability Assessment", description: "Evaluation of growth potential and scalability of your business" },
    { icon: Search, title: "Market Research", description: "In-depth UK market research specific to your industry" },
    { icon: Users, title: "Competitor Analysis", description: "Comprehensive analysis of competitors in your target market" },
    { icon: FileText, title: "Full Technical Proposal", description: "Complete technical business plan ready for endorsement" },
    { icon: Calculator, title: "Full Financial Forecast", description: "Detailed 3-5 year financial projections" },
    { icon: BarChart3, title: "Revenue Projections", description: "Realistic revenue forecasts with multiple scenarios" },
    { icon: PieChart, title: "Cashflow Forecasts", description: "Monthly cashflow projections and analysis" },
    { icon: Briefcase, title: "Investment Planning", description: "Funding requirements and investment strategy" },
    { icon: Shield, title: "Risk Assessment", description: "Comprehensive risk analysis and mitigation strategies" },
    { icon: Award, title: "Endorsement Readiness Review", description: "Final review ensuring you meet all endorsement criteria" },
    { icon: FileCheck, title: "Unlimited Revisions During Draft", description: "As many revisions as needed until you're satisfied" },
    { icon: Clock, title: "Two Live Zoom Consultations", description: "One-on-one video calls to discuss your plan" },
    { icon: Phone, title: "Priority WhatsApp Support", description: "Direct access to Ahmad via WhatsApp for quick queries" },
    { icon: Globe, title: "Final Word + PDF Submission Pack", description: "Professional documents ready to submit to endorsing bodies" },
  ];

  // Build services list without using .map() in JSX
  const serviceCards = [];
  for (let i = 0; i < services.length; i++) {
    const service = services[i];
    const IconComponent = service.icon;
    serviceCards.push(
      <motion.div 
        key={i}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: i * 0.05 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
      >
        <div className="w-12 h-12 rounded-xl gradient-red flex items-center justify-center mb-4">
          <IconComponent className="w-6 h-6 text-white" />
        </div>
        <h3 className="font-bold text-slate-900 mb-2">{service.title}</h3>
        <p className="text-sm text-slate-600">{service.description}</p>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header" data-testid="nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                <span className="text-slate-900">innovator</span>
                <span className="text-gradient-red">foundervisa</span>
              </span>
            </Link>
            
            <Link to="/">
              <Button variant="ghost" className="btn-ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-wide">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="badge-red mb-4">Premium Service</Badge>
            <h1 className="text-4xl md:text-5xl font-black mb-6">
              White-Glove
              <span className="text-gradient-red"> Business Plan Service</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Let me handle everything for you. I'll write your complete Innovator Founder Visa business plan from scratch, tailored to your unique idea.
            </p>
            <div className="text-center mb-8">
              <span className="text-6xl font-black text-slate-900">
                {loading ? '...' : `£${premiumPrice.toLocaleString()}`}
              </span>
              <p className="text-slate-500 mt-2">One-time payment • Full service</p>
            </div>
            <Link to="/register">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started Now
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container-wide">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              All <span className="text-gradient-red">17 Services</span> Included
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Every aspect of your Innovator Founder Visa application covered by an expert who has been through the process successfully.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCards}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-gradient text-white">
        <div className="container-wide text-center">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black mb-6">
              Ready to Get Your Visa
              <span className="text-gradient-red"> Approved?</span>
            </h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              Stop struggling with complex requirements. Let me write your business plan while you focus on building your business.
            </p>
            <Link to="/register">
              <Button className="btn-primary text-lg px-10 py-6 glow-red">
                Start Your Application
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">innovatorfoundervisas.com</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/support" className="text-slate-400 hover:text-white transition-colors text-sm">
                Support
              </Link>
              <p className="text-slate-400 text-sm text-center">
                © 2026 innovatorfoundervisas.com. Created by Ahmad Raza - UK Innovator Founder Visa Holder
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default WhiteGloveService;
